import React, { useEffect, useMemo, useState } from "react";
import { StatusBadge, StatusType } from "../../components/reusable components/StatusBadge";
import {
  getContactMethodChoices,
  getOutcomeChoices,
  getSavedGrantsOverview,
  getStatusChoices,
  postContactMethodGrant,
  postFilesApplication,
  postOutcomeGrant,
  postSavedGrant,
  postStatusGrant,
  SavedGrant,
  savedGrantDelete,
} from "../../api/organizer";
import toast from "react-hot-toast";
import { OutcomeBadge, OutcomeType } from "../../components/reusable components/OutcomeBadge";
import { ContactMethodBadge, ContactMethodType } from "../../components/reusable components/ContactMethodBadge";
import { getGrantFullDetails } from "../../api/grantWriting";
import { useNavigate } from "react-router-dom";
import { postGrantFullDetails } from "../../api/discovery";
import Select from "../../components/ui/Select";
import { patchSavedGrantDetails } from "../../lib/api";

// ─────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────

const UploadIcon = () => <span className="material-icons text-[20px]">upload</span>;
const EyeIcon   = () => <span className="material-icons text-[20px]">visibility</span>;

// ─────────────────────────────────────────────────────────────
// SKELETON PRIMITIVES
// ─────────────────────────────────────────────────────────────

const Skel = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

/** Skeleton for the Active Grants table */
function SkeletonGrantsTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* fake header */}
      <div className="grid grid-cols-5 gap-4 px-6 py-4" style={{ backgroundColor: "#C0EFC0" }}>
        {["Grant Name", "Status", "Deadline", "Activity", "Actions"].map((h) => (
          <div key={h} className="text-sm font-semibold text-gray-800 uppercase tracking-wider">{h}</div>
        ))}
      </div>
      <div className="divide-y divide-gray-100">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="grid grid-cols-5 gap-4 px-6 py-4 items-center">
            <Skel className="h-4 w-3/4" />
            {/* status — centered */}
            <div className="flex justify-center"><Skel className="h-6 w-24 rounded-full" /></div>
            <Skel className="h-4 w-2/3" />
            <Skel className="h-4 w-1/2" />
            <div className="flex gap-2">
              <Skel className="h-7 w-14 rounded" />
              <Skel className="h-7 w-12 rounded" />
              <Skel className="h-7 w-16 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton for the Past Applications table */
function SkeletonPastTable({ rows = 4 }: { rows?: number }) {
  const cols = 9;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr style={{ backgroundColor: "#C0EFC0" }}>
              {["Grant Name","Outcome","Deadline","Decision Due","Amount Awarded","Contact Method","Feedback","Comments","Actions"].map((h) => (
                <th key={h} className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider border border-[#0000001A]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {Array.from({ length: rows }).map((_, i) => (
              <tr key={i}>
                <td className="px-6 py-4 border border-[#0000001A]"><Skel className="h-4 w-32" /></td>
                {/* outcome — centered */}
                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="flex justify-center"><Skel className="h-6 w-24 rounded-full" /></div>
                </td>
                <td className="px-6 py-4 border border-[#0000001A]"><Skel className="h-4 w-20" /></td>
                <td className="px-6 py-4 border border-[#0000001A]"><Skel className="h-4 w-20" /></td>
                <td className="px-6 py-4 border border-[#0000001A]"><Skel className="h-4 w-12" /></td>
                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="flex justify-center"><Skel className="h-6 w-24 rounded-full" /></div>
                </td>
                <td className="px-6 py-4 border border-[#0000001A]"><Skel className="h-8 w-20 rounded-lg" /></td>
                <td className="px-6 py-4 border border-[#0000001A]"><Skel className="h-4 w-28" /></td>
                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="flex gap-2">
                    <Skel className="h-7 w-12 rounded-lg" />
                    <Skel className="h-7 w-20 rounded-lg" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

const timeAgo = (iso?: string | null) => {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const mapActiveStatusToBadge = (s: SavedGrant["status"]): StatusType => {
  switch (s) {
    case "in_progress": return "In Progress";
    case "applied":     return "Applied";
    case "pending":     return "Awaited";
    case "approved":    return "Approved";
    case "rejected":    return "Rejected";
    case "saved":       return "Saved";
    case "draft":       return "Draft"; 
    case "archived":    return "Archived";
    case "awarded":    return "Awarded";
    default:            return "Not Started";   
  }
};

const isoDateOnly = (iso?: string | null) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  } catch { return (iso || "").split("T")[0] || ""; }
};

// ─────────────────────────────────────────────────────────────
// ROW TYPES
// ─────────────────────────────────────────────────────────────

type GrantRow = {
  id: number;
  grant: string;
  name: string;
  status: SavedGrant["status"] | "";
  deadline: string;
  activity: string;
  updated_at: string;
};

const toActiveRow = (g: any): GrantRow => ({
  id: g.id,
  grant: g.grant,
  name: g.grant_title || (g.grant && (g.grant.title || g.grant.name)) || "—",
  status: g.status || "",
  deadline: fmtDate(g.grant_deadline),
  activity: g.activity || timeAgo(g.updated_at),
  updated_at: g.updated_at,
});

type PastRow = {
  id: number;
  grant: string;
  name: string;
  outcome: string | "";
  status: string;
  feedback: "Upload" | "View";
  comments: string;
  deadline: string;
  attachments: any;
  decision_due: string;
  decision_due_raw?: string | null;
  contact_method: string;
  total_funding_awarded: number | null;
};

const toPastRow = (g: SavedGrant): PastRow => ({
  id: (g as any).id,
  name: (g as any).grant_title || ((g as any).grant && ((g as any).grant.title || (g as any).grant.name)) || "—",
  outcome: (g as any).outcome || "",
  status: (g as any).status,
  grant: (g as any).grant,
  feedback: ((g as any).attachments && (g as any).attachments.length ? "View" : "Upload"),
  comments: (g as any).comments ?? (g as any).notes ?? "—",
  deadline: fmtDate((g as any).grant_deadline),
  attachments: (g as any).attachments || null,
  decision_due: fmtDate((g as any).decision_due),
  decision_due_raw: (g as any).decision_due || null,
  contact_method: (g as any).contact_method || "—",
  total_funding_awarded: (g as any).total_funding_awarded ?? null,
});

type OutcomeChoice       = { value: string; label: string };
type ContactMethodChange = { value: string; label: string };

// ─────────────────────────────────────────────────────────────
// ACTIVE GRANTS TABLE
// ─────────────────────────────────────────────────────────────

const GrantsTable: React.FC<{
  rows: GrantRow[];
  loading?: boolean;
  onRefresh: () => void;
  statusChoices: { value: string; label: string }[];
  onStatusChange: (id: number, newStatus: string) => void;
  onOpenGrant: (grant: GrantRow) => void;
  openEdit: (grant: GrantRow) => void;
  setDeleteId: (id: number) => void;
  setShowDeleteModal: (val: boolean) => void;
}> = ({ rows, loading, onRefresh, statusChoices, onStatusChange, onOpenGrant, openEdit, setDeleteId, setShowDeleteModal }) => {

  if (loading) return <SkeletonGrantsTable rows={5} />;

  const getStatusTypeFromValue = (value: string): StatusType => {
    const choice = statusChoices.find((c) => c.value === value);
    return (choice?.label as StatusType) || "Not Started";
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-green-200" style={{ backgroundColor: "#C0EFC0" }}>
              {["Grant Name", "Status", "Deadline", "Activity", "Actions"].map((h) => (
                <th key={h} className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider border border-[#0000001A]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50 transition-colors duration-200">
                <td className="px-6 py-4 text-sm text-[#141414] font-medium border border-[#0000001A]">{r.name}</td>

                {/* ✅ STATUS — centered */}
                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="relative flex justify-center items-center">
                    <select
                      value={r.status}
                      onChange={(e) => onStatusChange(r.id, e.target.value)}
                      style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer", zIndex: 10 }}
                    >
                      <option value="" disabled>Select status</option>
                      {statusChoices.map((choice) => (
                        <option key={choice.value} value={choice.value}>{choice.label}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1 pointer-events-none">
                      <StatusBadge status={getStatusTypeFromValue(r.status)} />
                      <span className="material-icons text-gray-400 text-[18px]">arrow_drop_down</span>
                    </div>
                  </div>
                </td>

                <td className="px-6 py-4 text-sm text-[#777B7F] border border-[#0000001A]">{r.deadline}</td>
                <td className="px-6 py-4 text-sm text-[#777B7F] border border-[#0000001A]">{r.activity}</td>
                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="flex items-center gap-2">
                    <button className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium hover:text-blue-700" onClick={() => onOpenGrant(r)}>Open</button>
                    <button className="inline-flex items-center gap-1 px-3 py-1 text-sm hover:text-gray-700" onClick={() => openEdit(r)}>Edit</button>
                    <button
                      className="inline-flex items-center underline gap-1 px-3 py-1 text-sm font-medium hover:text-red-700"
                      onClick={() => { setDeleteId(r.id); setShowDeleteModal(true); }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td className="px-6 py-8 text-sm text-gray-500" colSpan={5}>No active grants yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PAST APPLICATIONS TABLE
// ─────────────────────────────────────────────────────────────

const PastApplicationsTable: React.FC<{
  rows: PastRow[];
  loading?: boolean;
  outcomeChoices: OutcomeChoice[];
  contactMethodChange: ContactMethodChange[];
  onOutcomeChange: (id: number, newOutcome: string) => void;
  onContactMethodChange: (id: number, newOutcome: string) => void;
  onRefresh: () => void;
  onEdit: (row: PastRow) => void;
}> = ({ rows, loading, outcomeChoices, onOutcomeChange, contactMethodChange, onContactMethodChange, onRefresh, onEdit }) => {

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<string | undefined>();

  if (loading) return <SkeletonPastTable rows={4} />;

  // const getOutcomeTypeFromValue = (value: string): OutcomeType => {
  //   const choice = outcomeChoices.find(c => c.value === value);
  //   return (choice?.value as OutcomeType) || "Awaiting Outcome";
  // };
  const getOutcomeTypeFromValue = (value: string): OutcomeType => {
  switch (value) {
    case "awaiting":
      return "Awaiting Decision";
    case "unsuccessful":
      return "Unsuccessful";
    case "awarded":
      return "Awarded";
    case "approved":
      return "Approved";
    default:
      return "Awaiting Decision";
  }
};
  const getContactMethodTypeFromValue = (value: string): ContactMethodType => {
    const choice = contactMethodChange.find(c => c.value === value);
    return (choice?.value as ContactMethodType) || "Awaiting Outcome";
  };

  const handleFilesAdd = async (application: any) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,application/pdf";
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        toast.loading("Uploading file...");
        await postFilesApplication({ grant: application.grant, saved_grant: application.id, files: file });
        toast.dismiss();
        toast.success("File uploaded successfully!");
        onRefresh();
      } catch (err: any) {
        toast.dismiss();
        toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail||"Failed to upload file");
      }
    };
    input.click();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-green-200" style={{ backgroundColor: "#C0EFC0" }}>
              {["Grant Name","Outcome","Deadline","Decision Due","Amount Awarded","Contact Method","Feedback","Comments","Actions"].map((h) => (
                <th key={h} className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider border border-[#0000001A]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((application) => (
              <tr key={application.id} className="hover:bg-gray-50 transition-colors duration-200">
                <td className="px-6 py-4 text-sm font-medium text-[#141414] border border-[#0000001A]">{application.name}</td>

                {/* ✅ OUTCOME — centered */}
                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="relative flex justify-center items-center">
                    <select
                      value={application.outcome}
                      onChange={(e) => onOutcomeChange(application.id, e.target.value)}
                      style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer", zIndex: 10 }}
                    >
                      <option value="" disabled>Select outcome</option>
                      {outcomeChoices.map((choice) => (
                        <option key={choice.value} value={choice.value}>{choice.label}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1 pointer-events-none">
                      <OutcomeBadge outcome={getOutcomeTypeFromValue(application.outcome)} />
                      <span className="material-icons text-gray-400 text-[18px]">arrow_drop_down</span>
                    </div>
                  </div>
                </td>

                <td className="px-6 py-4 text-sm text-[#777B7F] border border-[#0000001A]">{application.deadline}</td>
                <td className="px-6 py-4 text-sm text-[#777B7F] border border-[#0000001A]">{application.decision_due}</td>
                <td className="px-6 py-4 text-sm text-[#141414] border border-[#0000001A]">
                  {application.outcome === "awarded" ? (application.total_funding_awarded ?? "—") : "—"}
                </td>

                {/* ✅ CONTACT METHOD — centered */}
                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="relative flex justify-center items-center">
                    <select
                      value={application.contact_method}
                      onChange={(e) => onContactMethodChange(application.id, e.target.value)}
                      style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer", zIndex: 10 }}
                    >
                      <option value="" disabled>Select contact method</option>
                      {contactMethodChange.map((choice) => (
                        <option key={choice.value} value={choice.value}>{choice.label}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1 pointer-events-none">
                      <ContactMethodBadge method={getContactMethodTypeFromValue(application.contact_method)} />
                      <span className="material-icons text-gray-400 text-[18px]">arrow_drop_down</span>
                    </div>
                  </div>
                </td>

                <td className="px-6 py-4 border border-[#0000001A]">
                  <button
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      application.feedback === "Upload"
                        ? "bg-gray-800 text-white hover:bg-gray-900"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    onClick={() => {
                      if (application.feedback === "Upload") {
                        handleFilesAdd(application);
                      } else {
                        const fileUrl = application.attachments?.at(-1)?.file;
                        setPreviewFile(fileUrl);
                        setPreviewOpen(true);
                      }
                    }}
                  >
                    {application.feedback === "Upload" ? <UploadIcon /> : <EyeIcon />}
                    {application.feedback}
                  </button>
                </td>

                <td className="px-6 py-4 text-sm text-[#777B7F] border border-[#0000001A] align-top w-[280px] max-w-[280px]">
  <div className="whitespace-pre-wrap break-words">
    {application.comments || "—"}
  </div>
</td>                <td className="px-6 py-4 border border-[#0000001A]">
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200"
                      onClick={() => onEdit(application)}
                    >
                      Edit
                    </button>
                    {/* {application.outcome === "Unsuccessful" ? (
                      <button className="px-3 py-1 text-green-700 text-sm font-medium rounded-lg transition-colors duration-200" style={{ backgroundColor: "#93C893" }}>
                        Reapply
                      </button>
                    ) : (
                      <button className="px-3 py-1 bg-gray-100 text-gray-700 text-sm cursor-context-menu font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200">
                        Reapply
                      </button>
                    )} */}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td className="px-6 py-8 text-sm text-gray-500" colSpan={9}>No past applications.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <FeedbackPreviewModal
        open={previewOpen}
        fileUrl={previewFile}
        onClose={() => setPreviewOpen(false)}
        onReupload={() => {
          setPreviewOpen(false);
          const application = rows.find(r => r.attachments?.at(-1)?.file === previewFile);
          if (application) handleFilesAdd(application);
        }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// FEEDBACK PREVIEW MODAL
// ─────────────────────────────────────────────────────────────

const FeedbackPreviewModal: React.FC<{
  open: boolean;
  onClose: () => void;
  fileUrl?: string;
  onReupload: () => void;
}> = ({ open, onClose, fileUrl, onReupload }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl w-[80%] max-w-3xl h-[20vh] relative flex flex-col">
        <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-700" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>
        <div className="p-4 flex justify-between items-center border-b">
          <h3 className="text-lg font-semibold text-gray-800">Uploaded File Preview</h3>
        </div>
        <div className="flex-1 overflow-auto p-4 flex items-center justify-around gap-4">
          <button className="px-4 py-2 bg-[#93C893] text-white rounded-lg mr-2 hover:bg-[#7dbd7d]">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">Open File</a>
          </button>
          <button className="px-4 py-2 bg-[#93C893] text-white rounded-lg hover:bg-[#7dbd7d]" onClick={onReupload}>
            Reupload
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ADD GRANT MODAL
// ─────────────────────────────────────────────────────────────

const STATUSES: SavedGrant["status"][] = ["saved","in_progress","applied","draft","archived","pending","approved","rejected"];

const AddGrantModal: React.FC<{ open: boolean; onClose: () => void; onCreated: () => Promise<void> | void }> = ({ open, onClose, onCreated }) => {
  const [status, setStatus]   = useState<SavedGrant["status"]>("saved");
  const [notes, setNotes]     = useState("");
  const [reminder, setReminder] = useState("");
  const [saving, setSaving]   = useState(false);

  useEffect(() => { if (open) { setStatus("saved"); setNotes(""); setReminder(""); } }, [open]);
  if (!open) return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      await postSavedGrant({ status, notes: notes.trim() || undefined, reminder_at: reminder ? new Date(reminder).toISOString() : undefined });
      toast.success("Grant added");
      onClose();
      await onCreated();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to add grant");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Add Active Grant</h3>
          <button onClick={onClose} aria-label="Close"><span className="material-icons">close</span></button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium mb-1">Status</div>
            <Select className="w-full border rounded-lg h-10 p-2" value={status} onChange={(e) => setStatus(e.target.value as SavedGrant["status"])}>
              <option value="" disabled>Select status</option>
              {STATUSES.map((s) => (<option key={s} value={s}>{mapActiveStatusToBadge(s)}</option>))}
            </Select>
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Notes</div>
            <textarea className="w-full border rounded-lg p-3" rows={4} placeholder="Optional notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Reminder</div>
            <input type="datetime-local" className="w-full border rounded-lg h-10 px-3" value={reminder} onChange={(e) => setReminder(e.target.value)} />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button className="h-10 px-4 rounded-[10px] border bg-white" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="h-10 px-4 rounded-[10px] text-white" style={{ backgroundColor: "#93C893" }} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// GRANT DETAILS MODAL
// ─────────────────────────────────────────────────────────────

const GrantDetailsModal: React.FC<{ open: boolean; onClose: () => void; grant?: GrantRow | null }> = ({ open, onClose, grant }) => {
  const [detail, setDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!open || !grant) return;
    setDetail(null);
    setLoadingDetail(true);
    (async () => {
      try {
        const data = await postGrantFullDetails(grant.grant);
        setDetail(data);
      } catch { /* silent */ }
      finally { setLoadingDetail(false); }
    })();
  }, [open, grant]);

  if (!open || !grant) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-scroll p-6 pt-0 relative">
        <div className="sticky top-0 bg-white z-10 flex justify-between items-center py-3">
          <h2 className="text-xl font-semibold text-gray-800">Grant Details</h2>
          <button onClick={onClose}><span className="material-icons">close</span></button>
        </div>

        {loadingDetail ? (
          <div className="space-y-3 py-2">
            {[1,2,3,4,5,6].map((i) => (
              <div key={i} className="flex gap-3">
                <Skel className="h-4 w-28 flex-shrink-0" />
                <Skel className="h-4 flex-1" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3 text-sm text-gray-700">
            <p><strong>Name:</strong> {grant.name}</p>
            <p><strong>Application Type:</strong> {detail?.applicant_type ?? "—"}</p>
            <p><strong>Description:</strong> {detail?.description ?? "—"}</p>
            <p><strong>Funder:</strong> {detail?.funder ?? "—"}</p>
            <p><strong>Location:</strong> {detail?.location ?? "—"}</p>
            <p><strong>Source Name:</strong> {detail?.source_name ?? "—"}</p>
            <p><strong>Total Fund Amount:</strong> {detail?.total_fund_amount ?? "—"}</p>
            <p><strong>Status:</strong> {grant.status}</p>
            <p><strong>Deadline:</strong> {grant.deadline}</p>
            <p><strong>Activity:</strong> {grant.activity}</p>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Close</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// EDIT PAST APPLICATION MODAL
// ─────────────────────────────────────────────────────────────

const EditPastApplicationModal: React.FC<{
  open: boolean;
  onClose: () => void;
  application: PastRow | null;
  onSaved: () => void;
}> = ({ open, onClose, application, onSaved }) => {
  const [decisionDue, setDecisionDue] = useState("");
  const [comments, setComments]       = useState("");
  const [amount, setAmount]           = useState("");
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    if (open && application) {
      setDecisionDue(isoDateOnly(application.decision_due_raw || null));
      setComments(application.comments && application.comments !== "—" ? application.comments : "");
      setAmount(application.total_funding_awarded != null ? String(application.total_funding_awarded) : "");
    } else { setDecisionDue(""); setComments(""); setAmount(""); }
  }, [open, application]);

  if (!open || !application) return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload: any = {
        outcome: application.outcome || null,
        decision_due: decisionDue || null,
        comments: comments || null,
      };
      if (application.outcome === "awarded") payload.total_funding_awarded = amount !== "" ? Number(amount) : null;
      await patchSavedGrantDetails(application.id, payload);
      toast.success("Application updated");
      onClose();
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Edit Application</h3>
          <button onClick={onClose} aria-label="Close"><span className="material-icons">close</span></button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium mb-1">Decision Due</div>
            <input type="date" className="w-full border rounded-lg h-10 px-3" value={decisionDue} onChange={(e) => setDecisionDue(e.target.value)} />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Comments</div>
            <input type="text" className="w-full border rounded-lg h-10 px-3" placeholder="Add comments" value={comments} onChange={(e) => setComments(e.target.value)} />
          </div>
          {application.outcome === "awarded" && (
            <div>
              <div className="text-sm font-medium mb-1">Amount Awarded</div>
              <input type="number" min="0" className="w-full border rounded-lg h-10 px-3" placeholder="e.g. 5000" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button className="h-10 px-4 rounded-[10px] border bg-white" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="h-10 px-4 rounded-[10px] text-white" style={{ backgroundColor: "#93C893" }} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────

export default function Organize() {
  const [loading,              setLoading]              = useState(false);
  const [activeRows,           setActiveRows]           = useState<GrantRow[]>([]);
  const [pastRows,             setPastRows]             = useState<PastRow[]>([]);
  const [open,                 setOpen]                 = useState(false);
  const [refreshKey,           setRefreshKey]           = useState(0);
  const [outcomeChoices,       setOutcomeChoices]       = useState<OutcomeChoice[]>([]);
  const [contactMethodChange,  setContactMethodChange]  = useState<ContactMethodChange[]>([]);
  const [statusChoices,        setStatusChoices]        = useState<OutcomeChoice[]>([]);
  const [selectedGrant,        setSelectedGrant]        = useState<GrantRow | null>(null);
  const [detailsOpen,          setDetailsOpen]          = useState(false);
  const [recentUpdateTime,     setRecentUpdateTime]     = useState("");
  const [editOpen,             setEditOpen]             = useState(false);
  const [editingApplication,   setEditingApplication]   = useState<PastRow | null>(null);
  const [deleteId,             setDeleteId]             = useState<number | null>(null);
  const [showDeleteModal,      setShowDeleteModal]      = useState(false);

  const nav = useNavigate();

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const ov = await getSavedGrantsOverview();
      setActiveRows(ov.active_grants.map(toActiveRow));
      setPastRows(ov.past_applications.map(toPastRow));
    } catch (e: any) {
      toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to load grants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, [refreshKey]);

  // Load dropdown choices
  useEffect(() => {
    (async () => {
      try { setStatusChoices(await getStatusChoices()); } catch {}
      try { setOutcomeChoices(await getOutcomeChoices()); } catch {}
      try { setContactMethodChange(await getContactMethodChoices()); } catch {}
    })();
  }, []);

  const parseActivityToSeconds = (activity: string): number => {
    if (!activity) return Infinity;
    const parts = activity.replace("ago", "").trim().split(/[\s,]+/).filter(Boolean);
    let total = 0;
    for (let i = 0; i < parts.length; i += 2) {
      const num = parseInt(parts[i]);
      const unit = parts[i + 1]?.toLowerCase();
      if (isNaN(num) || !unit) continue;
      if (unit.startsWith("s")) total += num;
      else if (unit.startsWith("m")) total += num * 60;
      else if (unit.startsWith("h")) total += num * 3600;
      else if (unit.startsWith("d")) total += num * 86400;
      else if (unit.startsWith("w")) total += num * 604800;
    }
    return total;
  };

  useEffect(() => {
    const mostRecent = activeRows
      .filter(r => r.activity && r.activity !== "—")
      .sort((a, b) => parseActivityToSeconds(a.activity) - parseActivityToSeconds(b.activity))[0];
    if (mostRecent) setRecentUpdateTime(mostRecent.activity);
  }, [activeRows]);

  const handleOutcomeChange = async (id: number, newOutcome: string) => {
    try { await postOutcomeGrant(id, newOutcome); toast.success("Outcome updated"); fetchOverview(); }
    catch (e: any) { toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to update outcome"); }
  };
  const handleContactMethodChange = async (id: number, newMethod: string) => {
    try { await postContactMethodGrant(id, newMethod); toast.success("Contact Method updated"); fetchOverview(); }
    catch (e: any) { toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to update contact method"); }
  };
  const handleStatusChange = async (id: number, newStatus: string) => {
    try { await postStatusGrant(id, newStatus); toast.success("Status updated"); fetchOverview(); }
    catch (e: any) { toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to update status"); }
  };
  const handleOpenGrant = (grant: GrantRow) => { setSelectedGrant(grant); setDetailsOpen(true); };
  const handleEditChange = async (grant: GrantRow) => {
    try {
      const data = await getGrantFullDetails(grant.id);
      nav(`/dashboard/write/${grant.id}`, {
        state: { saved_grant_id: grant.id, grant: { id: grant.id, title: grant.name || "Selected Grant" }, details: data },
      });
    } catch (e) { 
      // console.error(e); }
    }
  };
  const handleOpenEditPast = (row: PastRow) => { setEditingApplication(row); setEditOpen(true); };

  const key = useMemo(() => `k-${refreshKey}`, [refreshKey]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-gray-800">Grant Organizer</h1>
            <p className="text-gray-600">Here is the Overview of your recent activities.</p>
          </div>
          <div className="text-right">
            <p className="mb-2 text-sm text-gray-500">Last update</p>
            {loading
              ? <Skel className="h-5 w-24 ml-auto" />
              : <p className="text-gray-700 font-medium">{recentUpdateTime || "—"}</p>
            }
          </div>
        </div>

        {/* Active Grants */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Active Grants</h2>
          </div>
          <div key={key}>
            <GrantsTable
              rows={activeRows}
              loading={loading}
              onRefresh={fetchOverview}
              statusChoices={statusChoices}
              onStatusChange={handleStatusChange}
              onOpenGrant={handleOpenGrant}
              openEdit={handleEditChange}
              setDeleteId={setDeleteId}
              setShowDeleteModal={setShowDeleteModal}
            />
          </div>
        </div>

        {/* Past Applications */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Past Applications</h2>
          <PastApplicationsTable
            rows={pastRows}
            loading={loading}
            outcomeChoices={outcomeChoices}
            onOutcomeChange={handleOutcomeChange}
            contactMethodChange={contactMethodChange}
            onContactMethodChange={handleContactMethodChange}
            onRefresh={fetchOverview}
            onEdit={handleOpenEditPast}
          />
        </div>
      </div>

      {/* Modals */}
      <GrantDetailsModal open={detailsOpen} onClose={() => setDetailsOpen(false)} grant={selectedGrant} />

      <EditPastApplicationModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        application={editingApplication}
        onSaved={fetchOverview}
      />

      {/* Delete confirmation */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-3 text-gray-800">Delete Grant</h2>
            <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete this grant? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowDeleteModal(false); setDeleteId(null); }}
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-sm">
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!deleteId) return;
                  try {
                    await savedGrantDelete(deleteId);
                    toast.success("Grant deleted successfully");
                    fetchOverview();
                  } catch (e: any) {
                    toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail|| "Failed to delete grant");
                  } finally { setShowDeleteModal(false); setDeleteId(null); }
                }}
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
